/**
 * Congress Trading API Service
 *
 * Fetches congressional stock trades data from the server.
 * Data is sourced from STOCK Act public disclosures.
 */

// =====================================================
// TYPES
// =====================================================

export interface CongressTrade {
  id: string;
  politician_name: string;
  chamber: 'house' | 'senate';
  party: string | null;
  state: string | null;
  district: string | null;
  transaction_date: string;
  disclosure_date: string | null;
  ticker: string | null;
  asset_description: string | null;
  asset_type: string | null;
  trade_type: 'purchase' | 'sale_full' | 'sale_partial' | 'exchange';
  amount_range: string | null;
  amount_low: number | null;
  amount_high: number | null;
  owner: string | null;
  comment: string | null;
  created_at: string;
}

export interface CongressPolitician {
  id: string;
  name: string;
  chamber: 'house' | 'senate';
  party: string | null;
  state: string | null;
  district: string | null;
  photo_url: string | null;
  is_featured: boolean;
  total_trades: number;
  total_volume_estimate: number;
  last_trade_date: string | null;
}

export interface TradesResponse {
  success: boolean;
  data: CongressTrade[];
  count: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  lastSync: string | null;
}

export interface PoliticiansResponse {
  success: boolean;
  data: CongressPolitician[];
  count: number;
}

export interface TradeFilters {
  politician?: string;
  ticker?: string;
  chamber?: 'house' | 'senate' | 'all';
  trade_type?: 'purchase' | 'sale' | 'sale_full' | 'sale_partial' | 'exchange' | 'all';
  days?: number;
  limit?: number;
  offset?: number;
}

// =====================================================
// API BASE
// =====================================================

const API_BASE = import.meta.env.DEV
  ? 'http://localhost:3000/api/congress'
  : '/api/congress';

async function fetchApi<T>(endpoint: string, params: Record<string, string> = {}): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });

  const response = await fetch(url.toString(), {
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(15000),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
}

// =====================================================
// API FUNCTIONS
// =====================================================

/**
 * Fetch congress trades with filters
 */
export async function fetchCongressTrades(filters: TradeFilters = {}): Promise<TradesResponse> {
  const params: Record<string, string> = {};

  if (filters.politician) params.politician = filters.politician;
  if (filters.ticker) params.ticker = filters.ticker;
  if (filters.chamber && filters.chamber !== 'all') params.chamber = filters.chamber;
  if (filters.trade_type && filters.trade_type !== 'all') params.trade_type = filters.trade_type;
  if (filters.days) params.days = String(filters.days);
  if (filters.limit) params.limit = String(filters.limit);
  if (filters.offset) params.offset = String(filters.offset);

  return fetchApi<TradesResponse>('/trades', params);
}

/**
 * Fetch list of politicians with trade stats
 */
export async function fetchPoliticians(featured = false): Promise<PoliticiansResponse> {
  const params: Record<string, string> = { politicians: 'true' };
  if (featured) params.featured = 'true';
  return fetchApi<PoliticiansResponse>('/trades', params);
}

/**
 * Fetch trades for a specific politician
 */
export async function fetchPoliticianTrades(
  name: string,
  options: { days?: number; limit?: number; offset?: number } = {}
): Promise<TradesResponse> {
  return fetchCongressTrades({
    politician: name,
    days: options.days || 365,
    limit: options.limit || 100,
    offset: options.offset || 0,
  });
}

// =====================================================
// UTILITIES
// =====================================================

/**
 * Format amount range for display
 */
export function formatAmountRange(low: number | null, high: number | null): string {
  if (!low || !high) return 'N/A';
  const formatNum = (n: number) => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n}`;
  };
  return `${formatNum(low)} - ${formatNum(high)}`;
}

/**
 * Get trade type label
 */
export function getTradeTypeLabel(type: string): string {
  switch (type) {
    case 'purchase': return 'Buy';
    case 'sale_full': return 'Sell (Full)';
    case 'sale_partial': return 'Sell (Partial)';
    case 'exchange': return 'Exchange';
    default: return type;
  }
}

/**
 * Get trade type color class
 */
export function getTradeTypeColor(type: string): string {
  switch (type) {
    case 'purchase': return 'text-green-400';
    case 'sale_full': return 'text-red-400';
    case 'sale_partial': return 'text-orange-400';
    case 'exchange': return 'text-blue-400';
    default: return 'text-gray-400';
  }
}

/**
 * Get party color
 */
export function getPartyColor(party: string | null): string {
  switch (party) {
    case 'Democrat': return 'text-blue-400';
    case 'Republican': return 'text-red-400';
    case 'Independent': return 'text-purple-400';
    default: return 'text-gray-400';
  }
}

/**
 * Get party badge color
 */
export function getPartyBadgeColor(party: string | null): string {
  switch (party) {
    case 'Democrat': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'Republican': return 'bg-red-500/20 text-red-300 border-red-500/30';
    case 'Independent': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  }
}

/**
 * Calculate estimated trade volume for a politician
 */
export function calculateVolume(trades: CongressTrade[]): number {
  return trades.reduce((sum, t) => {
    if (t.amount_low && t.amount_high) {
      return sum + (t.amount_low + t.amount_high) / 2;
    }
    return sum;
  }, 0);
}

/**
 * Format relative date
 */
export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
